import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import {
  Button,
  CssBaseline,
  Paper,
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SecurityIcon from '@mui/icons-material/Security';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import axios from '../api/axios';
import { BrowserTitle } from '../components';

const buttonStyles = {
  mt: 3,
  mb: 2,
  p: 1.25,
};

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        setError('No reset token provided');
        return;
      }

      try {
        const response = await axios.post('/auth/validate-reset-token', { token });
        if (response.data.valid) {
          setTokenValid(true);
          setMaskedEmail(response.data.email || '');
        } else {
          setError('This password reset link is invalid or has expired.');
        }
      } catch (err) {
        setError('This password reset link is invalid or has expired.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: '', color: 'error' };
    
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 1;

    if (score <= 2) return { score: 25, label: 'Weak', color: 'error' };
    if (score <= 3) return { score: 50, label: 'Fair', color: 'warning' };
    if (score <= 4) return { score: 75, label: 'Good', color: 'info' };
    return { score: 100, label: 'Strong', color: 'success' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/auth/reset-password', { token, password });
      setSuccess(true);
      toast.success('Password has been reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(password);

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={4}
        md={8}
        sx={{
          backgroundImage: 'url(../images/login_bg.jpeg)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: (t) => (t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900]),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <Grid item xs={12} sm={8} md={4} component={Paper} elevation={6} square sx={{ backgroundColor: '#F0F3F5' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            padding: '0 60px',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <img src="/images/Airmaxx-Logo-Trans.png" alt="Logo" style={{ height: 60 }} />
          </Box>

          <BrowserTitle title="Reset Password | Airmaxx Pro" />

          {validating ? (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Validating reset link...</Typography>
            </Box>
          ) : success ? (
            <SuccessView navigate={navigate} />
          ) : !tokenValid ? (
            <InvalidTokenView error={error} navigate={navigate} />
          ) : (
            <ResetForm
              maskedEmail={maskedEmail}
              password={password}
              setPassword={setPassword}
              confirmPassword={confirmPassword}
              setConfirmPassword={setConfirmPassword}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              showConfirmPassword={showConfirmPassword}
              setShowConfirmPassword={setShowConfirmPassword}
              passwordStrength={passwordStrength}
              loading={loading}
              handleSubmit={handleSubmit}
              navigate={navigate}
            />
          )}
        </Box>
      </Grid>
      <ToastContainer />
    </Grid>
  );
};

const SuccessView = ({ navigate }) => (
  <Box sx={{ textAlign: 'center' }}>
    <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
    <Typography variant="h5" sx={{ fontFamily: '"Days One", sans-serif', color: '#0D3B59', mb: 2 }}>
      Password Reset Complete
    </Typography>
    <Typography sx={{ mb: 3, fontFamily: '"Lato", sans-serif' }}>
      Your password has been successfully reset. You can now log in with your new password.
    </Typography>
    <Button
      fullWidth
      variant="contained"
      onClick={() => navigate('/login')}
      sx={{ ...buttonStyles, fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
    >
      Go to Login
    </Button>
  </Box>
);

const InvalidTokenView = ({ error, navigate }) => (
  <Box sx={{ textAlign: 'center' }}>
    <LockResetIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
    <Typography variant="h5" sx={{ fontFamily: '"Days One", sans-serif', color: '#0D3B59', mb: 2 }}>
      Invalid Reset Link
    </Typography>
    <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
      {error}
    </Alert>
    <Typography sx={{ mb: 3, fontFamily: '"Lato", sans-serif' }}>
      Please request a new password reset link.
    </Typography>
    <Button
      fullWidth
      variant="contained"
      onClick={() => navigate('/login')}
      sx={{ ...buttonStyles, fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
    >
      Back to Login
    </Button>
  </Box>
);

const ResetForm = ({
  maskedEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  showPassword,
  setShowPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  passwordStrength,
  loading,
  handleSubmit,
  navigate,
}) => {
  const [generating, setGenerating] = useState(false);

  const generatePassword = async () => {
    setGenerating(true);
    try {
      const response = await axios.get('/auth/generate-password');
      const newPassword = response.data.password;
      setPassword(newPassword);
      setConfirmPassword(newPassword);
      setShowPassword(true);
      toast.success('Secure password generated! Make sure to save it before resetting.');
    } catch (err) {
      toast.error('Failed to generate password');
    } finally {
      setGenerating(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(password);
    toast.info('Password copied to clipboard!');
  };

  return (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <LockResetIcon sx={{ fontSize: 48, color: '#0D3B59' }} />
    </Box>

    <Typography
      variant="h5"
      sx={{ fontFamily: '"Days One", sans-serif', color: '#0D3B59', textAlign: 'center', mb: 1 }}
    >
      Reset Your Password
    </Typography>

    {maskedEmail && (
      <Typography
        variant="body2"
        sx={{ fontFamily: '"Lato", sans-serif', textAlign: 'center', mb: 2, color: 'text.secondary' }}
      >
        For account: {maskedEmail}
      </Typography>
    )}

    {/* 2FA Recommendation Banner */}
    <Box
      sx={{
        background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
        borderRadius: 2,
        p: 2,
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
      }}
    >
      <SecurityIcon sx={{ color: '#fff', fontSize: 32 }} />
      <Box>
        <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.95rem' }}>
          🔐 Enable Two-Factor Authentication
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.8rem' }}>
          After login, go to Profile → Security to enable 2FA for enhanced account protection.
        </Typography>
      </Box>
    </Box>

    {/* Generate Password Button */}
    <Button
      fullWidth
      variant="outlined"
      startIcon={generating ? <CircularProgress size={20} /> : <AutorenewIcon />}
      onClick={generatePassword}
      disabled={generating}
      sx={{
        mb: 2,
        py: 1.5,
        fontFamily: '"Lato", sans-serif',
        textTransform: 'none',
        borderWidth: 2,
        '&:hover': { borderWidth: 2 },
      }}
    >
      Generate Secure Password (Recommended)
    </Button>

    <FormControl sx={{ mt: 1, width: '100%' }} variant="outlined">
      <InputLabel htmlFor="new-password">New Password *</InputLabel>
      <OutlinedInput
        id="new-password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        endAdornment={
          <InputAdornment position="end">
            {password && (
              <IconButton
                onClick={copyPassword}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
                title="Copy password"
                sx={{ color: 'text.secondary' }}
              >
                <ContentCopyIcon />
              </IconButton>
            )}
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              onMouseDown={(e) => e.preventDefault()}
              edge="end"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
        label="New Password *"
        sx={{ backgroundColor: '#fff' }}
      />
    </FormControl>

    {password && (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption">Password Strength</Typography>
          <Typography variant="caption" color={`${passwordStrength.color}.main`}>
            {passwordStrength.label}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={passwordStrength.score}
          color={passwordStrength.color}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>
    )}

    <FormControl sx={{ mt: 2, width: '100%' }} variant="outlined">
      <InputLabel htmlFor="confirm-password">Confirm Password *</InputLabel>
      <OutlinedInput
        id="confirm-password"
        type={showConfirmPassword ? 'text' : 'password'}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={confirmPassword && password !== confirmPassword}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              onMouseDown={(e) => e.preventDefault()}
              edge="end"
            >
              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
        label="Confirm Password *"
        sx={{ backgroundColor: '#fff' }}
      />
    </FormControl>

    {confirmPassword && password !== confirmPassword && (
      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
        Passwords do not match
      </Typography>
    )}

    <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
      Password must be at least 8 characters long. For best security, use a mix of uppercase, lowercase, numbers, and symbols.
    </Typography>

    <Button
      fullWidth
      variant="contained"
      onClick={handleSubmit}
      disabled={!password || password.length < 8 || password !== confirmPassword || loading}
      sx={{ ...buttonStyles, fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
    >
      {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
    </Button>

    <Box sx={{ textAlign: 'center' }}>
      <Button
        variant="text"
        onClick={() => navigate('/login')}
        sx={{ fontFamily: '"Lato", sans-serif', textTransform: 'none' }}
      >
        Back to Login
      </Button>
    </Box>
  </Box>
  );
};

export default ResetPassword;
